import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Hash, ChevronLeft, Clock, Phone, MapPin, Car, Building, Sparkles, Loader2, ArrowRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_ANALYSES = [
  { id: 'phone', label: 'Téléphone', icon: Phone, placeholder: '06 12 34 56 78' },
  { id: 'address', label: 'Adresse', icon: MapPin, placeholder: '12 rue de la Paix' },
  { id: 'plate', label: 'Plaque', icon: Car, placeholder: 'AB-123-CD' },
  { id: 'company', label: 'Entreprise', icon: Building, placeholder: 'Nom de société' },
];

const MirrorHourResult = ({ interpretation }) => {
  if (!interpretation) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border border-purple-200"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
          <Clock className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-purple-600 font-medium">{interpretation.type}</p>
          <p className="text-2xl font-bold text-purple-800">{interpretation.time}</p>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-3xl font-bold text-purple-700">{interpretation.number}</span>
          {interpretation.meaning?.name && (
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
              {interpretation.meaning.name}
            </span>
          )}
        </div>
        
        <p className="text-purple-700">{interpretation.message}</p>
        
        {interpretation.meaning?.keywords && (
          <div className="flex flex-wrap gap-2 pt-2">
            {interpretation.meaning.keywords.map((kw, i) => (
              <span key={i} className="px-2 py-1 bg-white/50 text-purple-600 rounded-full text-xs">
                {kw}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const NumberAnalysisResult = ({ analysis }) => {
  if (!analysis || analysis.error) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
          <Hash className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-emerald-600 font-medium">Analyse</p>
          <p className="text-lg font-bold text-emerald-800">{analysis.original}</p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-baseline gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase">Chiffres</p>
            <p className="text-xl font-mono text-gray-700">{analysis.digits}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Somme</p>
            <p className="text-xl font-bold text-gray-700">{analysis.sum}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Réduit</p>
            <p className="text-3xl font-bold text-emerald-700">{analysis.reduced}</p>
          </div>
        </div>
        
        {analysis.meaning && (
          <div className="pt-4 border-t border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-emerald-800">{analysis.meaning.name}</span>
            </div>
            
            <p className="text-emerald-700 mb-3">{analysis.vibration}</p>
            
            <div className="flex flex-wrap gap-2">
              {analysis.meaning.keywords?.slice(0, 4).map((kw, i) => (
                <span key={i} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                  {kw}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const NumerologiaDailyPage = () => {
  const navigate = useNavigate();
  
  // Mirror Hour state
  const [hour, setHour] = useState('');
  const [minute, setMinute] = useState('');
  const [mirrorResult, setMirrorResult] = useState(null);
  const [loadingMirror, setLoadingMirror] = useState(false);
  const [mirrorInterpretation, setMirrorInterpretation] = useState(null);
  const [loadingMirrorInterpretation, setLoadingMirrorInterpretation] = useState(false);
  
  // Number Analysis state
  const [numberInput, setNumberInput] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [numberInterpretation, setNumberInterpretation] = useState(null);
  const [loadingNumberInterpretation, setLoadingNumberInterpretation] = useState(false);

  const checkMirrorHour = async () => {
    const h = parseInt(hour);
    const m = parseInt(minute);
    
    if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
      toast.error('Veuillez entrer une heure valide');
      return;
    }
    
    setLoadingMirror(true);
    setMirrorInterpretation(null);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/daily/mirror-hour`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hour: h, minute: m })
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (data.is_significant) {
          setMirrorResult(data.interpretation);
          toast.success('Heure significative !');
        } else {
          setMirrorResult(null);
          toast.info(data.message);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoadingMirror(false);
    }
  };

  const getMirrorInterpretation = async () => {
    if (!mirrorResult) return;
    
    setLoadingMirrorInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/daily/interpret`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_type: 'mirror_hour',
          data: {
            time: mirrorResult.time,
            number: mirrorResult.number,
            meaning: mirrorResult.meaning
          },
          context: null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMirrorInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingMirrorInterpretation(false);
    }
  };

  const analyzeNumber = async (text = null) => {
    const inputText = text || numberInput;
    
    if (!inputText.trim()) {
      toast.error('Veuillez entrer un nombre à analyser');
      return;
    }
    
    setLoadingAnalysis(true);
    setNumberInterpretation(null);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/daily/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: inputText })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAnalysisResult(data.analysis);
        toast.success('Analyse terminée !');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const getNumberInterpretation = async () => {
    if (!analysisResult) return;
    
    setLoadingNumberInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/daily/interpret`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_type: 'number',
          data: analysisResult,
          context: numberInput
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNumberInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingNumberInterpretation(false);
    }
  };

  const handleQuickAnalysis = (placeholder) => {
    setNumberInput(placeholder);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/numerologia')}
            className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-600" />
            <span className="font-heading text-lg font-bold text-emerald-700">
              Nombres du Quotidien
            </span>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Mirror Hours Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="h-7 w-7 text-white" />
              </div>
              <h2 className="font-heading text-xl font-bold text-gray-900">
                Heures miroir
              </h2>
              <p className="text-gray-600 text-sm">
                11:11, 22:22, 12:21... Que signifient ces synchronicités ?
              </p>
            </div>
            
            <div className="max-w-sm mx-auto">
              <div className="flex gap-2 items-center justify-center mb-4">
                <Input
                  type="number"
                  min="0"
                  max="23"
                  placeholder="HH"
                  data-testid="mirror-hour-input"
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  className="w-20 text-center text-xl"
                />
                <span className="text-2xl font-bold text-gray-400">:</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  placeholder="MM"
                  data-testid="mirror-minute-input"
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                  className="w-20 text-center text-xl"
                />
                <Button
                  onClick={checkMirrorHour}
                  disabled={loadingMirror}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
                  data-testid="check-mirror-btn"
                >
                  {loadingMirror ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                </Button>
              </div>
              
              {/* Quick mirror hour buttons */}
              <div className="flex flex-wrap gap-2 justify-center">
                {['11:11', '22:22', '12:21', '13:31', '14:41'].map((time) => {
                  const [h, m] = time.split(':');
                  return (
                    <button
                      key={time}
                      onClick={() => { setHour(h); setMinute(m); }}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm hover:bg-purple-200 transition-colors"
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {mirrorResult && (
              <div className="mt-6 space-y-4">
                <MirrorHourResult interpretation={mirrorResult} />
                
                {/* AI Interpretation for Mirror Hour */}
                {mirrorInterpretation ? (
                  <div className="p-6 bg-white rounded-2xl border border-purple-200">
                    <h4 className="font-heading text-md font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Interprétation IA
                    </h4>
                    <div className="prose prose-sm max-w-none text-gray-700">
                      <ReactMarkdown>{mirrorInterpretation}</ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <Button
                      onClick={getMirrorInterpretation}
                      disabled={loadingMirrorInterpretation}
                      variant="outline"
                      className="rounded-xl border-purple-300 text-purple-600 hover:bg-purple-50"
                      data-testid="get-mirror-interpretation-btn"
                    >
                      {loadingMirrorInterpretation ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Analyse...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Interprétation IA approfondie
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </motion.section>

          {/* Number Analysis Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Hash className="h-7 w-7 text-white" />
              </div>
              <h2 className="font-heading text-xl font-bold text-gray-900">
                Analyser un nombre
              </h2>
              <p className="text-gray-600 text-sm">
                Téléphone, adresse, plaque, nom d'entreprise...
              </p>
            </div>
            
            {/* Quick analysis buttons */}
            <div className="flex flex-wrap gap-3 justify-center mb-6">
              {QUICK_ANALYSES.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleQuickAnalysis(item.placeholder)}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:border-emerald-300 hover:bg-emerald-50 transition-all"
                  >
                    <Icon className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-gray-700">{item.label}</span>
                  </button>
                );
              })}
            </div>
            
            <div className="max-w-md mx-auto">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Entrez un nombre ou texte..."
                  data-testid="number-analysis-input"
                  value={numberInput}
                  onChange={(e) => setNumberInput(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={() => analyzeNumber()}
                  disabled={loadingAnalysis}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  data-testid="analyze-number-btn"
                >
                  {loadingAnalysis ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                </Button>
              </div>
            </div>
            
            {analysisResult && (
              <div className="mt-6 space-y-4">
                <NumberAnalysisResult analysis={analysisResult} />
                
                {/* AI Interpretation for Number */}
                {numberInterpretation ? (
                  <div className="p-6 bg-white rounded-2xl border border-emerald-200">
                    <h4 className="font-heading text-md font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Interprétation IA
                    </h4>
                    <div className="prose prose-sm max-w-none text-gray-700">
                      <ReactMarkdown>{numberInterpretation}</ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <Button
                      onClick={getNumberInterpretation}
                      disabled={loadingNumberInterpretation}
                      variant="outline"
                      className="rounded-xl border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                      data-testid="get-number-interpretation-btn"
                    >
                      {loadingNumberInterpretation ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Analyse...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Interprétation IA approfondie
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </motion.section>

          {/* Info Card */}
          <div className="p-6 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-2xl text-center">
            <Sparkles className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
            <h3 className="font-heading text-lg font-semibold text-emerald-800 mb-2">
              Les nombres vous parlent
            </h3>
            <p className="text-emerald-700 text-sm max-w-md mx-auto">
              Chaque nombre qui se répète ou attire votre attention porte un message.
              Restez attentif aux synchronicités !
            </p>
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/numerologia/cycles')}
              className="rounded-xl"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Cycles
            </Button>
            <Button
              onClick={() => navigate('/numerologia/karma')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            >
              Karma & Évolution
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NumerologiaDailyPage;
